import React from 'react'
import {Micropost} from '../types'
import {useFetchMicroposts} from '../services/useFetchMicroposts'
import {api} from '../services/api'
import {useRequiredAuth} from './AuthStore'
import {mergeMicropostsById, toHttpsUrl} from '../utils'

interface MicropostsInfo {
  selectedMicropost: Micropost | null
  microposts: Micropost[]
  hasError: boolean
  isLoading: boolean
  hasMore: boolean
  total: number
}

interface StatusReturn {
  status: 'success' | 'failure'
  message: string
}

export interface MicropostsStore extends MicropostsInfo {
  setSelectedMicropost: React.Dispatch<React.SetStateAction<Micropost | null>>
  updateMicropost(micropost: Micropost): Promise<StatusReturn>
  createMicropost(micropost: Micropost): Promise<StatusReturn>
  deleteMicropost(ID: Micropost['ID']): Promise<StatusReturn>
  maybeFetchMore: () => void
}

const MicropostsContext = React.createContext<MicropostsStore | undefined>(
  undefined,
)

export const useMicropostsStore = (): MicropostsStore => {
  const context = React.useContext(MicropostsContext)
  if (!context) {
    throw new Error(
      'useMicropostsStore must be used within a MicropostsProvider',
    )
  }

  return context
}

interface MicropostsContextProviderProps {
  children?: React.ReactNode
}

export const MicropostsProvider = ({
  children,
}: MicropostsContextProviderProps): React.ReactElement => {
  const [selectedMicropost, setSelectedMicropost] =
    React.useState<Micropost | null>(null)
  const [microposts, setMicroposts] = React.useState<Micropost[]>([])
  const {data, isLoading, hasError, maybeFetchMore, hasMore, total} =
    useFetchMicroposts() // TODO: Add control method when to fetch first page, not on mount like this currently is
  const {client, person} = useRequiredAuth()

  // Seed/append fetched pages into local state. This must be an effect, not a
  // useMemo: useMemo factories may re-run on renders where `data` is unchanged
  // (React 18 StrictMode / concurrent rendering), which would re-merge the
  // stale server snapshot over local mutations — making created posts vanish
  // and deleted posts reappear until a manual refresh. An effect runs only
  // when `data` actually changes (a real fetch / pagination).
  React.useEffect(() => {
    if (data.length === 0) return
    setMicroposts((prev) => mergeMicropostsById(prev, data))
  }, [data])

  const updateMicropost: MicropostsStore['updateMicropost'] = async (
    micropost,
  ) => {
    const response = await api.microposts.updateMicropost({
      clientID: client.ID,
      personID: person.ID,
      ID: micropost.ID,
      publishDate: micropost.publishDate,
      content: micropost.content,
      notify: micropost.notify,
      imageURL: micropost.imageURL,
      videoURL: micropost.videoURL ? toHttpsUrl(micropost.videoURL) : '',
      hyperlinkText:
        micropost.hyperlinkText || (micropost.hyperlinkURL ? 'Learn More' : ''), // If there is a URL, but no text, add one.
      hyperlinkURL: micropost.hyperlinkURL
        ? toHttpsUrl(micropost.hyperlinkURL)
        : '',
      segmentID: micropost.segmentID,
    })

    if (response.result === 'failure') {
      return Promise.resolve({
        status: 'failure',
        message: 'Unable to Save Micropost',
      })
    }

    // Replace the client-side data with the new data. `map` replaces in place
    // and is a no-op when the ID isn't present — unlike index-splicing, which
    // corrupted the list on a -1 miss (dropped the last item and inserted the
    // record anyway).
    setMicroposts((prev) =>
      prev.map((mp) => (mp.ID === response.data.ID ? response.data : mp)),
    )

    return Promise.resolve({
      status: 'success',
      message: 'Micropost Saved',
    })
  }

  const createMicropost: MicropostsStore['createMicropost'] = async (
    micropost,
  ) => {
    const response = await api.microposts.createMicropost({
      clientID: client.ID,
      personID: person.ID,
      ID: micropost.ID,
      publishDate: new Date(Date.now()), // This let's us mock Date.now in tests
      content: micropost.content,
      imageURL: micropost.imageURL,
      notify: micropost.notify,
      videoURL: micropost.videoURL ? toHttpsUrl(micropost.videoURL) : '',
      hyperlinkText:
        micropost.hyperlinkText || (micropost.hyperlinkURL ? 'Learn More' : ''), // If there is a URL, but no text, add one.
      hyperlinkURL: micropost.hyperlinkURL
        ? toHttpsUrl(micropost.hyperlinkURL)
        : '',
      segmentID: micropost.segmentID,
    })

    if (response.result === 'failure') {
      return Promise.resolve({
        status: 'failure',
        message: response.error.message,
      })
    }

    // Update the client-side data with the new mp
    setMicroposts((prev) => [response.data, ...prev])

    return Promise.resolve({
      status: 'success',
      message: 'Micropost Saved',
    })
  }

  const deleteMicropost: MicropostsStore['deleteMicropost'] = async (ID) => {
    const response = await api.microposts.deleteMicropost({
      ID,
      clientID: client.ID,
    })

    if (response.result === 'failure') {
      return Promise.resolve({
        status: 'failure',
        message: response.error.message,
      })
    }

    const removedMP = microposts.find((m) => m.ID === ID)

    // Cleanup attached segments. It is now an orphan since only MP currently uses segments.
    // Guard the lookup: if the ID is not in the local list, there is nothing to
    // orphan-clean and nothing to remove (avoids a TypeError on the miss).
    if (removedMP?.segmentID) {
      await api.segments.deleteSegment({
        clientID: client.ID,
        ID: removedMP.segmentID,
      })
    }

    // Use the functional updater so the removal reads live state at commit time.
    // Reading `microposts` from the render closure here would clobber any posts
    // a concurrent page-fetch effect appended while the delete API call was in
    // flight — dropping them silently.
    setMicroposts((prev) => prev.filter((m) => m.ID !== ID))

    return Promise.resolve({
      status: 'success',
      message: 'Micropost Deleted',
    })
  }

  // eslint-disable-next-line react/jsx-no-constructed-context-values
  const value = {
    selectedMicropost,
    setSelectedMicropost,
    microposts,
    deleteMicropost,
    isLoading,
    hasError,
    maybeFetchMore,
    hasMore,
    total,
    createMicropost,
    updateMicropost,
  }

  return (
    <MicropostsContext.Provider value={value}>
      {children}
    </MicropostsContext.Provider>
  )
}
